import { Card, Divider, Switch, Table, TablePaginationConfig } from "antd";
import { ColumnsType } from "antd/lib/table";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { generatePath, useNavigate } from "react-router-dom";
import { bindActionCreators } from "redux";
import {
  useDownloadInstitutionsExport,
  useFilterInstitutions,
  useUpdateInstitutionSettings
} from "../../../../common/api/queries";
import LabelWithTooltip from "../../../../common/components/form/labels/LabelWithTooltip";
import ActionTextIcon from "../../../../common/components/icons/ActionTextIcon";
import Ellipsis from "../../../../common/components/views/Ellipsis";
import { TableSizes } from "../../../../common/constants";
import { ExportFileType } from "../../../../common/enums";
import { Permission } from "../../../../common/security/authorization/enums";
import { RootState } from "../../../../common/types";
import { useFilterSearchParams } from "../../../../common/utils/hooksUtils";
import { URLQuery } from "../../../../common/utils/queryUtils";
import { numberOrZero, paginationTableProps, tableStandardProps } from "../../../../common/utils/utils";
import { selectHasPermissions } from "../../../auth/ducks";
import { getEnumerationsActions } from "../../../enumerations/ducks";
import { INSTITUTION_ROUTE_PATHS } from "../../paths";
import { InstitutionFilterPageRequest, InstitutionList } from "../../types";
import InstitutionTypeTagWithName from "../InstitutionTypeTagWithName";

export const InstitutionListTableView = () => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const searchParams = useFilterSearchParams<InstitutionFilterPageRequest>({ params: ["types"] });

  const { data } = useFilterInstitutions(searchParams);
  const updateInstitutionSettings = useUpdateInstitutionSettings();
  const downloadInstitutionsExport = useDownloadInstitutionsExport();

  const hasAdminGlobalSettingsPermission = useSelector<RootState, boolean>(state =>
    selectHasPermissions(Permission.ADMIN_GLOBAL_ENUMERATIONS)(state)
  );

  const dispatch = useDispatch();
  const actions = useMemo(
    () => bindActionCreators({ getEnumerations: getEnumerationsActions.request }, dispatch),
    [dispatch]
  );

  const handleUpdateInstitutionSettings = async (institution: InstitutionList) => {
    if (institution.settings) {
      await updateInstitutionSettings.mutateAsync({
        id: institution.id,
        object: {
          optimisticLockVersion: institution.settings.optimisticLockVersion,
          deactivated: !institution.settings.deactivated
        }
      });

      actions.getEnumerations();
    }
  };

  const columns: ColumnsType<InstitutionList> = [
    {
      key: "name",
      title: t("institution.attrs.name"),
      width: 260,
      ellipsis: { showTitle: false },
      render: (_, record) => <InstitutionTypeTagWithName institution={record} ellipsis />
    },
    {
      key: "officialName",
      title: t("institution.attrs.officialName"),
      width: 450,
      ellipsis: { showTitle: false },
      render: (_, record) => <Ellipsis>{record.officialName}</Ellipsis>
    },
    {
      key: "activated",
      title: (
        <LabelWithTooltip label={t("institution.attrs.activated")} tooltip={t("institution.helpers.deactivatedDesc")} />
      ),
      align: "center",
      fixed: "right",
      width: 100,
      render: (_, record) => (
        <Switch
          size="small"
          checked={!record.settings?.deactivated}
          onChange={() => handleUpdateInstitutionSettings(record)}
        />
      )
    },
    {
      key: "actions",
      align: "right",
      fixed: "right",
      width: 90,
      render: (_, record) =>
        hasAdminGlobalSettingsPermission && (
          <ActionTextIcon
            path={generatePath(INSTITUTION_ROUTE_PATHS.detail.to, { id: record.id })}
            icon="edit"
            color="blue"
            text={t("common.edit")}
          />
        )
    }
  ];

  const handleTableChange = (pagination: TablePaginationConfig) => {
    navigate(
      {
        search: URLQuery.searchParamsToString({
          searchParams: {
            pageIndex: pagination.current ? pagination.current - 1 : undefined
          },
          append: true
        })
      },
      { replace: true }
    );
  };

  return (
    <Card className="card-box">
      <div className={numberOrZero(data?.totalElementsCount) ? "table-header-export-actions" : "margin-bottom-small"}>
        <ActionTextIcon
          icon="download"
          color="blue"
          text={t("common.exportXlsx")}
          onClick={() => downloadInstitutionsExport.mutate({ ...searchParams, exportFileType: ExportFileType.XLSX })}
        />

        <Divider type="vertical" />

        <ActionTextIcon
          icon="download"
          color="green"
          text={t("common.exportCsv")}
          onClick={() => downloadInstitutionsExport.mutate({ ...searchParams, exportFileType: ExportFileType.CSV })}
        />
      </div>

      <Table<InstitutionList>
        {...tableStandardProps()}
        columns={columns}
        dataSource={data?.pageData}
        scroll={{ x: TableSizes.MEDIUM }}
        pagination={{
          ...paginationTableProps,
          current: numberOrZero(data?.pageIndex) + 1,
          pageSize: data?.pageSize,
          total: data?.totalElementsCount
        }}
        onChange={handleTableChange}
      />
    </Card>
  );
};
